import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import { Pagination } from '../../../../../components/ui/Pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { formatWeldStartEndRange } from '../../../../../lib/formatUtils';
import { weldDurationDisplay } from '../../../utils/weldDurationDisplay';
import { outcomeLabel, rowKey } from '../../../utils/weldDrillDownModel';
import { glossaryText } from '../../../utils/weldFieldGlossary';
import { AnalyticsDataTable, type AnalyticsColumn } from '../../shared/AnalyticsDataTable';
import { ColumnHeaderHelp } from '../../shared/ColumnHeaderHelp';
import { failedParamSummaryForRow, worstDeviationForRow } from '../../../utils/useWpsViolationOverview';
import type { WpsViolationRecord } from '../../../utils/wpsViolationModel';
import type { WeldDrillDownRow } from './drillDownTypes';

type NdtFilter = 'all' | 'none' | 'has_records' | 'rejected';
type OutcomeFilter = 'all' | 'pass' | 'fail';

function rowDurationLabel(row: WeldDrillDownRow): string {
  return weldDurationDisplay(row).label;
}

function ndtCountLabel(row: WeldDrillDownRow): string {
  if (row.ndtLogCount == null) return '—';
  return String(row.ndtLogCount);
}

function outcomeBadgeClass(outcome: WeldDrillDownRow['outcome']): string {
  if (outcome === 'pass') return 'bg-[rgba(31,154,58,0.08)] text-galvanizing-green border-galvanizing-green/30';
  if (outcome === 'fail') return 'bg-[rgba(196,0,31,0.06)] text-readable-red border-hero-red-20';
  return 'bg-[rgba(0,39,63,0.04)] text-heritage-grey border-[#E2E5EA]';
}

function matchesNdtFilter(badge: WeldDrillDownRow['ndtBadge'], filter: NdtFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'none') return badge === 'none';
  if (filter === 'has_records') return badge === 'has_records';
  return badge === 'rejected';
}

interface WeldDrillDownTableProps {
  rows: WeldDrillDownRow[];
  selectedRowKey: string | null;
  onRowSelect: (row: WeldDrillDownRow) => void;
  showOutcomeColumn?: boolean;
  emptyMessage?: string;
  showViolationColumns?: boolean;
  violationsByRowKey?: Record<string, WpsViolationRecord[]>;
}

export const WeldDrillDownTable = ({
  rows,
  selectedRowKey,
  onRowSelect,
  showOutcomeColumn = false,
  emptyMessage = 'No welds in this category for the selected jobs.',
  showViolationColumns = false,
  violationsByRowKey = {},
}: WeldDrillDownTableProps) => {
  const [search, setSearch] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [ndtFilter, setNdtFilter] = useState<NdtFilter>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const jobOptions = useMemo(() => {
    const jobs = new Set<string>();
    rows.forEach((row) => {
      const job = row.jobNumber.trim();
      if (job) jobs.add(job);
    });
    return Array.from(jobs).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (jobFilter !== 'all' && row.jobNumber !== jobFilter) return false;
      if (q) {
        const weldMatch = row.weldNumber.toLowerCase().includes(q);
        const jobMatch = row.jobNumber.toLowerCase().includes(q);
        if (!weldMatch && !jobMatch) return false;
      }
      if (!matchesNdtFilter(row.ndtBadge, ndtFilter)) return false;
      if (showOutcomeColumn && outcomeFilter !== 'all' && row.outcome !== outcomeFilter) {
        return false;
      }
      return true;
    });
  }, [rows, search, jobFilter, ndtFilter, outcomeFilter, showOutcomeColumn]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, safePage, pageSize]);

  const columns: AnalyticsColumn<WeldDrillDownRow & { slNo: number }>[] = [
    {
      id: 'slNo',
      header: 'Sl.no',
      cell: (row) => <span className="text-heritage-grey">{row.slNo}</span>,
      className: 'w-12 text-center',
    },
    {
      id: 'weldNumber',
      header: 'Weld #',
      cell: (row) => <span className="font-medium text-blueprint-blue">{row.weldNumber}</span>,
      className: 'whitespace-nowrap',
      headerClassName: 'whitespace-nowrap',
    },
    {
      id: 'jobNumber',
      header: 'Job #',
      cell: (row) => (
        <span className="whitespace-nowrap text-text-grey">{row.jobNumber || '—'}</span>
      ),
      className: 'min-w-[12rem] whitespace-nowrap',
      headerClassName: 'min-w-[12rem] whitespace-nowrap',
    },
    {
      id: 'timeRange',
      header: 'Start → End',
      cell: (row) => {
        const range = formatWeldStartEndRange(row.startTimeMs, row.endTimeMs);
        return (
          <div className="leading-tight">
            <div>{range.dateLine}</div>
            {range.timeLine !== '—' && (
              <div className="text-xs text-heritage-grey">{range.timeLine}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'duration',
      header: <ColumnHeaderHelp label="Weld Duration" help={glossaryText('drilldown_duration')} />,
      cell: (row) => rowDurationLabel(row),
    },
    ...(showViolationColumns
      ? [
          {
            id: 'failedParams',
            header: (
              <ColumnHeaderHelp
                label="Failed params"
                help="WPS parameters out of limit for this weld."
              />
            ),
            cell: (row: WeldDrillDownRow & { slNo: number }) => (
              <span className="max-w-[14rem] whitespace-normal break-words text-xs text-readable-red">
                {failedParamSummaryForRow(row, violationsByRowKey)}
              </span>
            ),
            className: 'min-w-[10rem] max-w-[14rem]',
          } as AnalyticsColumn<WeldDrillDownRow & { slNo: number }>,
          {
            id: 'worstDeviation',
            header: (
              <ColumnHeaderHelp
                label="Worst Δ%"
                help="Largest deviation from WPS limit among failed parameters."
              />
            ),
            cell: (row: WeldDrillDownRow & { slNo: number }) => (
              <span className="text-xs text-blueprint-blue">
                {worstDeviationForRow(row, violationsByRowKey)}
              </span>
            ),
          } as AnalyticsColumn<WeldDrillDownRow & { slNo: number }>,
        ]
      : []),
    {
      id: 'ndt',
      header: <ColumnHeaderHelp label="NDT" help={glossaryText('drilldown_ndt_badge')} />,
      cell: (row) => (
        <span className="tabular-nums text-blueprint-blue">{ndtCountLabel(row)}</span>
      ),
      className: 'w-14 text-center',
    },
    ...(showOutcomeColumn
      ? [
          {
            id: 'outcome',
            header: <ColumnHeaderHelp label="Outcome" help={glossaryText('wps_status_active')} />,
            cell: (row: WeldDrillDownRow & { slNo: number }) => (
              <span
                className={clsx(
                  'inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase',
                  outcomeBadgeClass(row.outcome),
                )}
              >
                {outcomeLabel(row.outcome)}
              </span>
            ),
          } as AnalyticsColumn<WeldDrillDownRow & { slNo: number }>,
        ]
      : []),
  ];

  const tableData = pageRows.map((row, idx) => ({
    ...row,
    slNo: (safePage - 1) * pageSize + idx + 1,
  }));

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1">
      <div className="flex w-full flex-nowrap items-center gap-2">
        <div className="relative min-w-[9rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-heritage-grey" />
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search weld # or job…"
            className="box-border h-9 w-full rounded-lg border border-[#E2E5EA] bg-white py-0 pl-8 pr-3 text-sm leading-none text-text-grey placeholder:text-heritage-grey focus:border-blueprint-blue focus:outline-none focus:ring-1 focus:ring-blueprint-blue/20"
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Select
            value={jobFilter}
            onValueChange={(v) => {
              setJobFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="box-border h-9 w-auto shrink-0 py-0 text-xs">
              <SelectValue placeholder="Job" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Job: All</SelectItem>
              {jobOptions.map((job) => (
                <SelectItem key={job} value={job}>
                  Job: {job}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={ndtFilter}
            onValueChange={(v) => {
              setNdtFilter(v as NdtFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="box-border h-9 w-auto shrink-0 py-0 text-xs">
              <SelectValue placeholder="NDT" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">NDT: All</SelectItem>
              <SelectItem value="none">NDT: None</SelectItem>
              <SelectItem value="has_records">NDT: Has logs</SelectItem>
              <SelectItem value="rejected">NDT: Rejected</SelectItem>
            </SelectContent>
          </Select>
          {showOutcomeColumn && (
            <Select
              value={outcomeFilter}
              onValueChange={(v) => {
                setOutcomeFilter(v as OutcomeFilter);
                setPage(1);
              }}
            >
              <SelectTrigger className="box-border h-9 w-auto shrink-0 py-0 text-xs">
                <SelectValue placeholder="Outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Outcome: All</SelectItem>
                <SelectItem value="pass">Within limit</SelectItem>
                <SelectItem value="fail">Out of limit</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <AnalyticsDataTable
        columns={columns}
        data={tableData}
        rowKey={(row) => rowKey(row)}
        emptyMessage={emptyMessage}
        onRowClick={onRowSelect}
        selectedRowKey={selectedRowKey}
        fillHeight
        className="min-h-0 flex-1"
      />

      <Pagination
        currentPage={safePage}
        totalItems={filteredRows.length}
        itemsPerPage={pageSize}
        onPageChange={setPage}
        onItemsPerPageChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        pageSizeOptions={[10, 25, 50]}
      />
    </div>
  );
};
